import type { AdminFlowRunGraphProps } from "../../models/admin-flows.models";
import { ADMIN_FLOW_PANEL } from "../../utils/admin-flows.constants";
import { useId } from "react";

// Render the frozen graph with its real connections, including separate roots and merges.
// Selecting a block changes the review inspector only; this graph never edits execution evidence.
const AdminFlowRunGraph = ({ run, onSelect }: AdminFlowRunGraphProps) => {
  const marker = `run-arrow-${useId().replace(/:/g, "")}`;
  const nodes = new Map(run.flow.nodes.map((node) => [node.id, node]));
  const left = Math.min(0, ...run.flow.nodes.map((node) => node.x)) - 20;
  const top = Math.min(0, ...run.flow.nodes.map((node) => node.y)) - 20;
  const width = Math.max(500, ...run.flow.nodes.map((node) => node.x + 220)) - left;
  const height = Math.max(240, ...run.flow.nodes.map((node) => node.y + 110)) - top;

  return (
    <div className={`${ADMIN_FLOW_PANEL} p-4`} data-test="admin-flow-executed-graph">
      <h3 className="font-semibold">Executed flow</h3>
      <svg
        viewBox={`${left} ${top} ${width} ${height}`}
        className="mt-3 max-h-96 min-h-48 w-full"
        role="group"
        aria-label="Frozen flow and execution states"
      >
        <defs>
          <marker id={marker} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <path d="M0 0 L8 4 L0 8" fill="#1F8475" />
          </marker>
        </defs>
        {run.flow.edges.map((edge) => {
          const from = nodes.get(edge.from);
          const to = nodes.get(edge.to);

          if (!from || !to) return null;

          const mid = (from.x + 200 + to.x) / 2;

          return (
            <path
              key={edge.id}
              d={`M${from.x + 200} ${from.y + 38} C${mid} ${from.y + 38}, ${mid} ${to.y + 38}, ${to.x} ${to.y + 38}`}
              fill="none"
              stroke="#1F8475"
              strokeWidth="2"
              markerEnd={`url(#${marker})`}
            />
          );
        })}
        {run.flow.nodes.map((node) => {
          const status = run.steps.find((step) => step.nodeId === node.id)?.status ?? "pending";
          const color =
            status === "completed"
              ? "#1F8475"
              : status === "failed"
                ? "#BF0002"
                : status === "awaiting_permission"
                  ? "#A66C00"
                  : "#64748b";

          return (
            <g
              key={node.id}
              role="button"
              tabIndex={0}
              data-test={`admin-flow-review-block-${node.id}`}
              aria-label={`${node.label}: ${status.replace(/_/g, " ")}`}
              onClick={() => onSelect(node.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(node.id);
                }
              }}
              className="cursor-pointer focus:outline focus:outline-2 focus:outline-green"
            >
              <title>{node.label}</title>
              <rect
                x={node.x}
                y={node.y}
                width="200"
                height="76"
                rx="5"
                fill="currentColor"
                className="text-white dark:text-gray-800"
                stroke={color}
                strokeWidth="2"
              />
              <text x={node.x + 12} y={node.y + 27} fontSize="13" className="fill-gray-900 dark:fill-gray-100">
                {node.label.length > 24 ? `${node.label.slice(0, 23)}…` : node.label}
              </text>
              <text x={node.x + 12} y={node.y + 51} fontSize="11" fill={color}>
                {status.replace(/_/g, " ")}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-xs opacity-60">Select a block to inspect its retained inputs and output.</p>
    </div>
  );
};

export default AdminFlowRunGraph;
